<template>
  <v-dialog
    v-model="show"
    max-width="900px"
  >
    <v-card>
      <v-card-title>
        Request for Equipment Update
        <v-spacer></v-spacer>
        <v-btn
          icon
          blur
          @click.prevent="show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="py-2">
        <v-row>
          <v-col cols="12" md="6">
            <v-select
              v-model="reasonType"
              :items="reasonTypeItems"
              item-value="value"
              item-text="label"
              label="Request type"
              :rules="[v => !!v || 'Request type is required']"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="equipmentCategory"
              :items="equipmentCategoryItems"
              item-value="value"
              item-text="label"
              label="Equipment category"
              :rules="[v => !!v || 'Equipment category is required']"
            ></v-select>
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="currentEquipment"
              label="Current equipment"
              hint="Model / inventory number (use information from Assets)"
              persistent-hint
              rows="1"
              auto-grow
            ></v-textarea>
          </v-col>
          <v-col cols="12" md="6">
            <v-textarea
              v-model="justification"
              label="Short description"
              hint="What is broken / what is needed"
              persistent-hint
              rows="1"
              auto-grow
              :rules="[v => !!(v && v.trim()) || 'Description is required']"
            ></v-textarea>
          </v-col>
          <v-col cols="12" md="6">
            <v-file-input
              label="Attachment (optional)"
              v-model="attachmentFile.file"
              @change="onAttachmentChange"
              show-size
              hint="Attach a photo or supporting evidence (if available)"
              persistent-hint
              accept=".doc, .docx, application/pdf, .jpg, .jpeg, .png, application/msword, image/jpg, image/jpeg, image/png"
            ></v-file-input>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              :items="filteredAccountManagers"
              label="Approver (Account Manager)"
              item-value="Employee_ID"
              item-text="Employee_Full_Name"
              v-model="accountManager"
              :rules="[v => !!v || 'Approver is required']"
            >
              <template v-slot:prepend-item>
                <div class="px-4 py-2">
                  <v-text-field
                    dense
                    hide-details
                    label="Search Approver"
                    v-model="searchAM"
                  ></v-text-field>
                </div>
                <v-divider></v-divider>
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-select
              v-model="urgency"
              :items="urgencyItems"
              item-value="value"
              item-text="label"
              label="Urgency"
              :rules="[v => !!v || 'Urgency is required']"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn small :loading="isLoading" :disabled="isLoading || !isRequestReady" @click="sendRequest" color="primary">Send</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'EquipmentReplacementRequest',
  props: {
    value: {
      required: true,
      default: false
    },
    accountManagers: {
      type: Array,
      default: () => []
    },
    requestId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      isLoading: false,
      accountManager: null,
      searchAM: '',
      reasonType: null,
      internalAccountManagers: [],
      reasonTypeItems: [
        { value: 'breakdown', label: 'Breakdown' },
        { value: 'issues', label: 'Issues' },
        { value: 'technical_limitations', label: 'Technical limitations' },
        { value: 'upgrade_needed', label: 'Upgrade needed' }
      ],
      equipmentCategory: null,
      equipmentCategoryItems: [
        { value: 'laptop', label: 'Laptop' },
        { value: 'desktop', label: 'Desktop' },
        { value: 'monitor', label: 'Monitor' },
        { value: 'other', label: 'Other' }
      ],
      currentEquipment: '',
      justification: '',
      urgency: 'standard',
      urgencyItems: [
        { value: 'standard', label: 'Standard' },
        { value: 'high', label: 'High' },
        { value: 'critical', label: 'Critical' }
      ],
      attachmentFile: {
        file: null,
        File_Name: '',
        File_Type: ''
      }
    }
  },
  mounted () {
    if (this.requestId) {
      this.loadRequestForEdit()
    }
  },
  computed: {
    show: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
        if (value) {
          this.$emit('getHistory')
        }
      }
    },
    user () {
      return this.$store.getters.getUser
    },
    amList () {
      return (this.accountManagers && this.accountManagers.length) ? this.accountManagers : this.internalAccountManagers
    },
    filteredAccountManagers () {
      if (!this.searchAM) return this.amList
      const r = new RegExp(this.searchAM, 'gi')
      return this.amList.filter(item => {
        r.lastIndex = 0
        return r.test(item.Employee_Full_Name)
      })
    },
    isRequestReady () {
      return !!this.accountManager && !!this.reasonType && !!this.equipmentCategory && !!this.urgency && !!(this.justification && this.justification.trim())
    }
  },
  methods: {
    onAttachmentChange (value) {
      const file = Array.isArray(value) ? value[0] : value
      if (!file || !(file instanceof File)) {
        this.attachmentFile = { file: null, File_Name: '', File_Type: '' }
        return
      }
      this.attachmentFile.File_Name = file.name || ''
      this.attachmentFile.File_Type = file.type || ''
      const reader = new FileReader()
      reader.onload = () => {
        let binary = ''
        const bytes = new Uint8Array(reader.result)
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i])
        }
        file.base64String = `data:${file.type};base64,${window.btoa(binary)}`
      }
      reader.readAsArrayBuffer(file)
    },
    sendRequest () {
      this.isLoading = true
      const headers = { Authorization: 'Bearer ' + this.user.token }

      if (this.requestId) {
        const payload = {
          Confirmer_ID: this.accountManager,
          Reason_Type: this.reasonType,
          Justification: this.justification.trim(),
          Equipment_Category: this.equipmentCategory,
          Current_Equipment: this.currentEquipment ? this.currentEquipment.trim() : null,
          Urgency: this.urgency
        }
        if (this.attachmentFile.file && this.attachmentFile.file.base64String) {
          payload.Confirmation_File = {
            file: { base64String: this.attachmentFile.file.base64String },
            File_Name: this.attachmentFile.File_Name,
            File_Type: this.attachmentFile.File_Type
          }
        }
        this.axios.put(`/api/requests/equipment_replacement_by_id/${this.requestId}`, payload, { headers })
          .then(async res => {
            const oldMainId = res.data[0] && res.data[0].Notification_ID
            const oldAdditionalId = res.data[0] && res.data[0].Additional_Notification_ID
            const deletes = []
            if (oldMainId) deletes.push(this.axios.delete(`/api/notifications/${oldMainId}`, { headers }))
            if (oldAdditionalId) deletes.push(this.axios.delete(`/api/notifications/${oldAdditionalId}`, { headers }))
            await Promise.all(deletes)
            const message = `Type: ${this.reasonTypeItems.find(r => r.value === this.reasonType)?.label || this.reasonType}. Category: ${this.equipmentCategoryItems.find(c => c.value === this.equipmentCategory)?.label || this.equipmentCategory}. Description: ${(this.justification || '').slice(0, 150)}${(this.justification || '').length > 150 ? '...' : ''}`
            const mainNotif = {
              Notification_Type_ID: 1,
              Notification_Title: `Equipment update request from ${this.user.full_name}`,
              Notification_Message: message,
              Notification_Created: new Date().toISOString().slice(0, 10),
              Notification_Employee_ID_From: this.user.employee_id,
              Notification_Employee_ID_To: this.accountManager
            }
            const notifRes = await this.axios.post('/api/notifications', mainNotif, { headers })
            const mainNotificationId = notifRes.data[0].Notification_ID
            await this.axios.put(`/api/requests/update_notification_equipment_replacement_request/${this.requestId}`, { Notification_ID: mainNotificationId }, { headers })
            await this.axios.put(`/api/requests/update_additional_notification_equipment_replacement_request/${this.requestId}`, { Additional_Notification_ID: null }, { headers })
            this.sendEmailAboutEquipmentReplacementRequest(this.requestId)
            this.show = false
            this.$store.dispatch('setSuccess', { successMsg: 'Equipment update request was updated.' })
            this.$emit('getHistory')
          })
          .catch(error => {
            this.$store.dispatch('setError', { errorMsg: (error.response && error.response.data) || error.message })
          })
          .finally(() => {
            this.isLoading = false
          })
        return
      }

      const message = `Type: ${this.reasonTypeItems.find(r => r.value === this.reasonType)?.label || this.reasonType}. Category: ${this.equipmentCategoryItems.find(c => c.value === this.equipmentCategory)?.label || this.equipmentCategory}. Description: ${(this.justification || '').slice(0, 150)}${(this.justification || '').length > 150 ? '...' : ''}`
      const mainNotif = {
        Notification_Type_ID: 1,
        Notification_Title: `Equipment update request from ${this.user.full_name}`,
        Notification_Message: message,
        Notification_Created: new Date().toISOString().slice(0, 10),
        Notification_Employee_ID_From: this.user.employee_id,
        Notification_Employee_ID_To: this.accountManager
      }
      this.axios.post('/api/notifications', mainNotif, { headers })
        .then(response => {
          const notificationId = response.data[0].Notification_ID
          const payload = {
            Creator_ID: this.user.employee_id,
            Confirmer_ID: this.accountManager,
            Notification_ID: notificationId,
            Reason_Type: this.reasonType,
            Justification: this.justification.trim(),
            Equipment_Category: this.equipmentCategory,
            Current_Equipment: this.currentEquipment ? this.currentEquipment.trim() : null,
            Urgency: this.urgency,
            Need_Additional_Approval: false,
            Additional_Confirmer_ID: null,
            Additional_Notification_ID: null
          }
          if (this.attachmentFile.file && this.attachmentFile.file.base64String) {
            payload.Confirmation_File = {
              file: { base64String: this.attachmentFile.file.base64String },
              File_Name: this.attachmentFile.File_Name,
              File_Type: this.attachmentFile.File_Type
            }
          }
          return this.axios.post('/api/requests/equipment_replacement', payload, { headers })
        })
        .then(result => {
          const requestId = result.data[0].Request_ID
          this.sendEmailAboutEquipmentReplacementRequest(requestId)
          this.show = false
          this.$store.dispatch('setSuccess', { successMsg: 'Equipment update request was submitted.' })
        })
        .catch(error => {
          this.$store.dispatch('setError', { errorMsg: (error.response && error.response.data) || error.message })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async loadRequestForEdit () {
      if (!this.amList.length) {
        try {
          const res = await this.axios.get('/api/l_and_d_approvers', {
            headers: { Authorization: 'Bearer ' + this.user.token }
          })
          this.internalAccountManagers = res.data || []
        } catch (e) {
          this.$store.dispatch('setError', { errorMsg: (e.response && e.response.data) || e.message })
          return
        }
      }
      this.axios.get(`/api/requests/equipment_replacement_by_id/${this.requestId}`, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).then(res => {
        const data = res.data[0]
        if (!data) return
        this.accountManager = data.Confirmer_ID
        this.reasonType = data.Reason_Type
        this.justification = data.Justification || ''
        this.equipmentCategory = data.Equipment_Category || null
        this.currentEquipment = data.Current_Equipment || ''
        this.urgency = data.Urgency || 'standard'
        this.attachmentFile.File_Name = data.Confirmation_File_Name || ''
        this.attachmentFile.File_Type = data.Confirmation_File_Type || ''
        if (data.Confirmation_File_Name) {
          this.axios.get(`/api/requests/equipment_replacement_file/${this.requestId}/Confirmation_File`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          }).then(fileRes => {
            const raw = fileRes.data[0] && fileRes.data[0].File
            if (raw && raw.data) {
              const bytes = new Uint8Array(raw.data)
              const blob = new Blob([bytes], { type: this.attachmentFile.File_Type })
              const file = new File([blob], this.attachmentFile.File_Name, { type: this.attachmentFile.File_Type })
              this.attachmentFile.file = file
            }
          }).catch(() => {})
        }
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: (error.response && error.response.data) || error.message })
      })
    },
    sendEmailAboutEquipmentReplacementRequest (requestId) {
      this.axios.post('/api/emails/sendemailforequipmentreplacementrequest', { Request_ID: requestId }, {
        headers: { Authorization: 'Bearer ' + this.user.token }
      }).catch(() => {})
    },
    clearAll () {
      this.accountManager = null
      this.searchAM = ''
      this.reasonType = null
      this.equipmentCategory = null
      this.currentEquipment = ''
      this.justification = ''
      this.urgency = 'standard'
      this.attachmentFile = { file: null, File_Name: '', File_Type: '' }
      this.internalAccountManagers = []
    }
  }
}
</script>
